/**
 * Azure Pub/Sub Chat Service
 *
 * IMPORTANT:
 * - Runtime identity must be UserID (string) for PubSub send_to_user routing.
 * - localMessageId MUST be generated once by caller and passed through to map server ack.
 */

import { getMultiHubSocketService } from '../pubsub';
import { v4 as uuidv4 } from 'uuid';

export interface ChatAttachment {
  url: string;
  name?: string;
  mimeType?: string;
  size?: number;
  kind?: 'image' | 'file';
  thumbUrl?: string;
}

export interface ChatMessage {
  id: string;
  chat_id: string;
  sender_id: string;
  content: string;
  timestamp: string;
  message_type: 'text' | 'file' | 'system' | 'notification';
  local_message_id?: string;
  attachments?: ChatAttachment[];
  status?: 'sending' | 'sent' | 'failed' | 'delivered';
  error?: string;
}

export type MessageCallback = (message: ChatMessage) => void;
export type TypingCallback = (data: { chat_id: string; user_id: string; stopped?: boolean }) => void;
export type ReadReceiptCallback = (data: { message_id: string; user_id: string; chat_id: string }) => void;
export type MessageDeliveredCallback = (data: { message_id: string; chat_id: string; timestamp?: string }) => void;

export type MessageSentAckCallback = (data: {
  chat_id: string;
  local_message_id: string;
  message_id: string;
  timestamp?: string;
  success?: boolean | string;
  error?: string;
}) => void;

class PubSubChatService {
  private pubsub = getMultiHubSocketService();
  private hub = 'messages';
  private listenersInitialized = false;

  private currentUserId: string | null = null;
  private activeChats: Set<string> = new Set();

  private messageCallbacks: Map<string, MessageCallback[]> = new Map();
  private typingCallbacks: Map<string, TypingCallback[]> = new Map();
  private readReceiptCallbacks: Map<string, ReadReceiptCallback[]> = new Map();
  private deliveredCallbacks: Map<string, MessageDeliveredCallback[]> = new Map();
  private connectionStatusCallbacks: Array<(connected: boolean) => void> = [];
  private messageSentCallbacks: Map<string, MessageSentAckCallback[]> = new Map();

  private messageAckMap: Map<string, string> = new Map();

  private recentlyAckedServerIds: Map<string, number> = new Map();
  private readonly ACK_ECHO_SUPPRESSION_MS = 2500;

  constructor(userId?: string) {
    if (userId) {
      this.currentUserId = String(userId);
      this.pubsub.setCurrentUserId(String(userId));
    }
  }

  async initialize(userId: string): Promise<boolean> {
    try {
      this.currentUserId = String(userId);
      // this.pubsub.setCurrentUserId(String(userId));

      const connected = await this.pubsub.connectToHub(this.hub);
      if (connected) {
        this.setupEventListeners();
        this.notifyConnectionStatus(true);
      }
      return connected;
    } catch (error) {
      return false;
    }
  }

  private setupEventListeners(): void {
    if (this.listenersInitialized) return;
    this.listenersInitialized = true;

    this.pubsub.on(this.hub, '*', (data: any, eventName: string) => {
      // MESSAGE
      if (eventName === 'message') {
        const chatId = String(data.chat_id);
        const msg = data as ChatMessage;

        const serverId = String(msg.id || '');
        if (serverId && this.shouldSuppressSenderEcho(serverId, msg.sender_id)) return;

        this.dispatchMessageCallbacks(chatId, msg);
        return;
      }

      // ACK: message_sent_<localId>
      if (eventName && eventName.startsWith('message_sent_')) {
        const localIdFromEvent = eventName.replace('message_sent_', '');
        const localId = String(data.local_message_id || data.localMessageId || localIdFromEvent || '');
        const serverId = String(data.message_id || data.messageId || '');
        const chatId = String(data.chat_id || '');

        // always dispatch ack even on failure
        this.dispatchMessageSentCallbacks(chatId, {
          chat_id: chatId,
          local_message_id: localId,
          message_id: serverId,
          timestamp: data.timestamp,
          success: data.success,
          error: data.error,
        });

        if (localId && serverId) {
          this.messageAckMap.set(localId, serverId);
          this.recentlyAckedServerIds.set(serverId, Date.now());
          this.cleanupAckCache();
        }
        return;
      }

      // DELIVERED
      if (eventName === 'message_delivered') {
        this.dispatchDeliveredCallbacks(String(data.chat_id), data);
        return;
      }

      // TYPING
      if (eventName === 'user_typing') {
        this.dispatchTypingCallbacks(String(data.chat_id), { ...data, stopped: false });
        return;
      }
      if (eventName === 'user_stop_typing') {
        this.dispatchTypingCallbacks(String(data.chat_id), { ...data, stopped: true });
        return;
      }

      // READ RECEIPT EVENT (incoming)
      if (eventName === 'message_read' || eventName === 'message_read_receipt') {
        this.dispatchReadReceiptCallbacks(String(data.chat_id), data);
        return;
      }
    });

    this.pubsub.on(this.hub, 'connect_error', (error: any) => {
      this.notifyConnectionStatus(false);
    });

    this.pubsub.on(this.hub, 'disconnect', () => {
      this.notifyConnectionStatus(false);
    });

    this.pubsub.on(this.hub, 'reconnect', () => {
      this.notifyConnectionStatus(true);
      this.rejoinChats();
    });
  }

  private shouldSuppressSenderEcho(serverId: string, senderId?: string): boolean {
    const ts = this.recentlyAckedServerIds.get(serverId);
    if (!ts) return false;

    const withinWindow = Date.now() - ts <= this.ACK_ECHO_SUPPRESSION_MS;
    if (!withinWindow) return false;

    if (senderId && this.currentUserId) return String(senderId) === String(this.currentUserId);
    return true;
  }

  private cleanupAckCache(): void {
    const now = Date.now();
    for (const [serverId, ts] of this.recentlyAckedServerIds.entries()) {
      if (now - ts > this.ACK_ECHO_SUPPRESSION_MS) this.recentlyAckedServerIds.delete(serverId);
    }
  }

  async joinChat(chatId: string): Promise<boolean> {
    if (!this.isConnected()) return false;
    if (!this.currentUserId) return false;

    const success = this.pubsub.emit(this.hub, 'join_chat', {
      chat_id: String(chatId),
      user_id: this.currentUserId,
    });

    if (success) this.activeChats.add(String(chatId));
    return success;
  }

  async leaveChat(chatId: string): Promise<boolean> {
    const success = this.pubsub.emit(this.hub, 'leave_chat', {
      chat_id: String(chatId),
      user_id: this.currentUserId,
    });

    if (success) {
      const id = String(chatId);
      this.activeChats.delete(id);
      this.messageCallbacks.delete(id);
      this.typingCallbacks.delete(id);
      this.readReceiptCallbacks.delete(id);
      this.deliveredCallbacks.delete(id);
      this.messageSentCallbacks.delete(id);
    }

    return success;
  }

  private rejoinChats(): void {
    if (!this.currentUserId) return;
    for (const chatId of this.activeChats) {
      this.pubsub.emit(this.hub, 'join_chat', { chat_id: String(chatId), user_id: this.currentUserId });
    }
  }

  /**
   * [OK] Send message with optional attachments.
   * Returns localMessageId. Server id comes via ack event.
   */
  async sendMessage(
    chatId: string,
    content: string,
    messageType: 'text' | 'file' = 'text',
    localMessageId?: string,
    attachments?: ChatAttachment[]
  ): Promise<string | null> {
    try {
      if (!this.currentUserId) return null;

      const finalLocalId = localMessageId || uuidv4();

      const payload: any = {
        chat_id: String(chatId),
        content,
        message_type: messageType,
        messageId: finalLocalId,
        local_message_id: finalLocalId,
        sender_id: this.currentUserId,
        user_id: this.currentUserId,
      };

      if (Array.isArray(attachments) && attachments.length) {
        payload.attachments = attachments;
      }

      const success = this.pubsub.emit(this.hub, 'send_message', payload);
      if (!success) return null;

      return finalLocalId;
    } catch (error) {
      return null;
    }
  }

  async sendTypingIndicator(chatId: string): Promise<boolean> {
    if (!this.currentUserId) return false;
    return this.pubsub.emit(this.hub, 'typing', { chat_id: String(chatId), user_id: this.currentUserId });
  }

  async sendStopTypingIndicator(chatId: string): Promise<boolean> {
    if (!this.currentUserId) return false;
    return this.pubsub.emit(this.hub, 'stop_typing', { chat_id: String(chatId), user_id: this.currentUserId });
  }

  async markMessageAsRead(chatId: string, messageId: string): Promise<boolean> {
    if (!this.currentUserId) return false;
    return this.pubsub.emit(this.hub, 'message_read', {
      chat_id: String(chatId),
      message_id: String(messageId),
      user_id: this.currentUserId,
    });
  }

  onMessage(chatId: string, callback: MessageCallback): () => void {
    const id = String(chatId);
    if (!this.messageCallbacks.has(id)) this.messageCallbacks.set(id, []);
    this.messageCallbacks.get(id)!.push(callback);
    return () => {
      const arr = this.messageCallbacks.get(id);
      if (!arr) return;
      const i = arr.indexOf(callback);
      if (i > -1) arr.splice(i, 1);
    };
  }

  onTyping(chatId: string, callback: TypingCallback): () => void {
    const id = String(chatId);
    if (!this.typingCallbacks.has(id)) this.typingCallbacks.set(id, []);
    this.typingCallbacks.get(id)!.push(callback);
    return () => {
      const arr = this.typingCallbacks.get(id);
      if (!arr) return;
      const i = arr.indexOf(callback);
      if (i > -1) arr.splice(i, 1);
    };
  }

  onReadReceipt(chatId: string, callback: ReadReceiptCallback): () => void {
    const id = String(chatId);
    if (!this.readReceiptCallbacks.has(id)) this.readReceiptCallbacks.set(id, []);
    this.readReceiptCallbacks.get(id)!.push(callback);
    return () => {
      const arr = this.readReceiptCallbacks.get(id);
      if (!arr) return;
      const i = arr.indexOf(callback);
      if (i > -1) arr.splice(i, 1);
    };
  }

  onMessageDelivered(chatId: string, callback: MessageDeliveredCallback): () => void {
    const id = String(chatId);
    if (!this.deliveredCallbacks.has(id)) this.deliveredCallbacks.set(id, []);
    this.deliveredCallbacks.get(id)!.push(callback);
    return () => {
      const arr = this.deliveredCallbacks.get(id);
      if (!arr) return;
      const i = arr.indexOf(callback);
      if (i > -1) arr.splice(i, 1);
    };
  }

  onMessageSent(chatId: string, callback: MessageSentAckCallback): () => void {
    const id = String(chatId);
    if (!this.messageSentCallbacks.has(id)) this.messageSentCallbacks.set(id, []);
    this.messageSentCallbacks.get(id)!.push(callback);
    return () => {
      const arr = this.messageSentCallbacks.get(id);
      if (!arr) return;
      const i = arr.indexOf(callback);
      if (i > -1) arr.splice(i, 1);
    };
  }

  onConnectionStatusChange(callback: (connected: boolean) => void): () => void {
    this.connectionStatusCallbacks.push(callback);
    return () => {
      const i = this.connectionStatusCallbacks.indexOf(callback);
      if (i > -1) this.connectionStatusCallbacks.splice(i, 1);
    };
  }

  private dispatchMessageCallbacks(chatId: string, message: ChatMessage): void {
    const callbacks = this.messageCallbacks.get(chatId);
    if (!callbacks) return;
    callbacks.forEach(cb => cb(message));
  }

  private dispatchTypingCallbacks(chatId: string, data: any): void {
    const callbacks = this.typingCallbacks.get(chatId);
    if (!callbacks) return;
    callbacks.forEach(cb => cb(data));
  }

  private dispatchReadReceiptCallbacks(chatId: string, data: any): void {
    const callbacks = this.readReceiptCallbacks.get(chatId);
    if (!callbacks) return;
    callbacks.forEach(cb => cb(data));
  }

  private dispatchDeliveredCallbacks(chatId: string, data: any): void {
    const callbacks = this.deliveredCallbacks.get(chatId);
    if (!callbacks) return;
    callbacks.forEach(cb => cb(data));
  }

  private dispatchMessageSentCallbacks(chatId: string, data: any): void {
    const callbacks = this.messageSentCallbacks.get(chatId);
    if (!callbacks) return;
    callbacks.forEach(cb => cb(data));
  }

  private notifyConnectionStatus(connected: boolean): void {
    this.connectionStatusCallbacks.forEach(cb => cb(connected));
  }

  isConnected(): boolean {
    return this.pubsub.isConnected(this.hub);
  }

  getActiveChats(): string[] {
    return Array.from(this.activeChats);
  }

  disconnect(): void {
    for (const chatId of this.activeChats) {
      this.pubsub.emit(this.hub, 'leave_chat', { chat_id: chatId, user_id: this.currentUserId });
    }
    this.activeChats.clear();
    this.pubsub.disconnectHub(this.hub);
  }

  getServerMessageId(localMessageId: string): string | undefined {
    return this.messageAckMap.get(String(localMessageId));
  }
}

// Singleton instance
let chatService: PubSubChatService | null = null;

export const getChatService = (userId?: string): PubSubChatService => {
  if (!chatService) chatService = new PubSubChatService(userId);
  return chatService;
};

export default PubSubChatService;
